import {
  createContext, useContext, ReactNode,
} from 'react';
import type { Auth0ContextInterface, User } from '@auth0/auth0-react';

// Default mock Auth0 user object for development and testing
const defaultMockUser: User = {
  sub: 'integration-test-user-123',
  user_id: 'integration-test-user-123',
  email: 'integrationtesting@creditpulse.com',
  name: 'integrationtesting@creditpulse.com',
  org_id: 'org_I4dcMvdY3LpxZw5q',
  'https://app.creditpulse.com/roles': [ 'SUPER_ADMIN' ], // Gives access to credit applications
};

// Factory function to create customizable mock Auth0 contexts
function createMockAuthContext (overrides?: Partial<Auth0ContextInterface<User>>): Auth0ContextInterface<User> {
  return {
    user: defaultMockUser,
    isAuthenticated: true,
    isLoading: false,
    error: undefined,
    loginWithRedirect: () => Promise.resolve(),
    loginWithPopup: () => Promise.resolve(),
    logout: () => Promise.resolve(),
    getAccessTokenSilently: () => Promise.resolve('507f1f77bcf86cd799439011_test-secret-key-123'),
    getAccessTokenWithPopup: () => Promise.resolve('507f1f77bcf86cd799439011_test-secret-key-123'),
    getIdTokenClaims: () => Promise.resolve(undefined),
    handleRedirectCallback: () => Promise.resolve({ appState: undefined }),
    ...overrides,
  } as Auth0ContextInterface<User>;
}

// Default context for development bypass
const mockAuthContext = createMockAuthContext();

const MockAuthContext = createContext<Auth0ContextInterface<User>>(mockAuthContext);

interface MockAuth0ProviderProps {
  children: ReactNode;
  authOverrides?: Partial<Auth0ContextInterface<User>>;
}

export function MockAuth0Provider ({ children, authOverrides = undefined }: MockAuth0ProviderProps) {
  const contextValue = authOverrides ? createMockAuthContext(authOverrides) : mockAuthContext;

  return (
    <MockAuthContext.Provider value={contextValue}>
      {children}
    </MockAuthContext.Provider>
  );
}

export function useMockAuth0 () {
  return useContext(MockAuthContext);
}

// Export factory function for test usage
export { createMockAuthContext };
